import type { Timestamp } from 'firebase/firestore'
import type { ClockCheckpointDoc } from './clock.ts'
import type {
  CarStanding,
  CarSummaryDoc,
  FcyPeriodDoc,
  FuelDoc,
  LapDoc,
  LiveMessage,
  MessageDoc,
  PitStopDoc,
  StandingsDoc,
  Stint,
  StintsDoc,
  StreamSessionDoc,
} from './timing.ts'

/**
 * A session as it stood at some earlier instant, rebuilt from what is stored.
 *
 * For replaying a race on a dashboard: everything a widget reads, cut to "at
 * or before" a moment that the viewer moves about. Most of the timing is
 * already a log with times on it — laps, stops, neutralisations, messages,
 * fills — and those are simply filtered. Three things are stored only as they
 * stand now, and are rebuilt here instead:
 *
 * - The standings, from each car's latest lap. A lap carries the position,
 *   gaps, driver and class as they were at the line, so the board moves once a
 *   lap rather than once a sector.
 * - The stints and the session bests, from the laps, the way the interpreter
 *   makes them (timing/internal/store/stints.go).
 * - The clock and the track state, from the final clock and the
 *   neutralisations.
 *
 * Nothing here knows about Firestore beyond the shape of its documents. The
 * caller says how to make a Timestamp, so this stays a pure function of what
 * it is given.
 */

export type Stamp = (at: Date) => Timestamp

/** Everything a replay is cut from: the session as it was finally stored. */
export interface ReplaySource {
  session: StreamSessionDoc | null
  /** The final standings — for team names, and which way the board sorts. */
  standings: StandingsDoc | null
  /** Every lap of every car. */
  laps: LapDoc[]
  pitStops: PitStopDoc[]
  fcyPeriods: FcyPeriodDoc[]
  messages: MessageDoc[]
  fuel: FuelDoc[]
}

/** A session at one instant, in the shapes the dashboard already reads. */
export interface ReplayFrame {
  at: Date
  session: StreamSessionDoc | null
  standings: StandingsDoc
  /** Every car's laps: the completed ones, and the one under way. */
  laps: LapDoc[]
  messages: MessageDoc[]
  liveMessages: { messages: LiveMessage[] }
  stints: StintsDoc[]
  pitStops: PitStopDoc[]
  fcyPeriods: FcyPeriodDoc[]
  fuel: FuelDoc[]
  carSummaries: CarSummaryDoc[]
}

/** The source, sorted once so that each frame is a cut rather than a search. */
export interface ReplayIndex {
  source: ReplaySource
  /** Each car's laps, by lap number. */
  byCar: Map<string, LapDoc[]>
  /**
   * How the board is ordered: by laps and then by who crossed the line first,
   * as a race is, or by best lap, as practice and qualifying are. Read off the
   * final standings rather than the session's name, which says "Course" or
   * "Q2" or nothing at all.
   */
  order: 'race' | 'best'
  teams: Map<string, string>
  /** The first and last instant worth replaying. */
  start: Date | null
  end: Date | null
}

/**
 * How long a message stays on the board in a replay.
 *
 * Nothing records when a message left the board — that is why the live board
 * is a document of its own — so a replay keeps each one up for this long.
 */
export const replayBoardMs = 10 * 60_000

const time = (t: Timestamp | null | undefined): number | null => (t ? t.toDate().getTime() : null)

export function indexReplay(source: ReplaySource): ReplayIndex {
  const byCar = new Map<string, LapDoc[]>()
  for (const l of source.laps) {
    if (!l.car) continue
    const list = byCar.get(l.car)
    if (list) list.push(l)
    else byCar.set(l.car, [l])
  }
  for (const list of byCar.values()) list.sort((a, b) => a.lap - b.lap)

  const teams = new Map<string, string>()
  for (const c of source.standings?.cars ?? []) if (c.number && c.team) teams.set(c.number, c.team)

  const index: ReplayIndex = { source, byCar, order: 'race', teams, ...replaySpan(source) }
  index.order = sortOrder(index)
  return index
}

/**
 * Where a replay runs from and to: the green flag, or the first lap anybody
 * started, to the flag, or the last lap anybody finished.
 */
export function replaySpan(source: ReplaySource): { start: Date | null; end: Date | null } {
  let first: number | null = null
  let last: number | null = null
  for (const l of source.laps) {
    const s = time(l.startedAt) ?? time(l.completedAt)
    const e = time(l.completedAt)
    if (s != null && (first == null || s < first)) first = s
    if (e != null && (last == null || e > last)) last = e
  }
  const session = source.session
  // The race clock's own zero — the final checkpoint less what had elapsed —
  // before clockStartAt, which can be left over from whatever ran earlier on
  // the channel: Portimão's race carried one from an hour and a quarter before
  // its start.
  const cp = session?.clock
  const zero = cp?.at && cp.elapsedMs > 0 ? cp.at.toDate().getTime() - cp.elapsedMs : null
  const start = zero ?? first ?? time(session?.clockStartAt) ?? time(session?.firstSeenAt)
  const end = time(session?.finishedAt) ?? last ?? time(session?.lastSeenAt)
  return {
    start: start != null ? new Date(start) : null,
    end: end != null ? new Date(Math.max(end, start ?? end)) : null,
  }
}

/** Which order the final board agrees with more. A race where it cannot tell. */
function sortOrder(index: ReplayIndex): 'race' | 'best' {
  const final = index.source.standings?.cars ?? []
  if (final.length < 2 || !index.end) return 'race'
  const vendor = [...final].filter((c) => c.position > 0).sort((a, b) => a.position - b.position)
  const agree = (order: 'race' | 'best') => {
    const ours = ordered(index, index.end!, order).map((c) => c.car)
    let same = 0
    vendor.forEach((c, i) => {
      if (ours[i] === c.number) same++
    })
    return same
  }
  return agree('best') > agree('race') ? 'best' : 'race'
}

interface CarAt {
  car: string
  last: LapDoc | null
  crossedAt: number | null
  best: number | null
  laps: number
}

/** Each car's standing at `at`, in board order. */
function ordered(index: ReplayIndex, at: Date, order: 'race' | 'best'): CarAt[] {
  const t = at.getTime()
  const out: CarAt[] = []
  for (const [car, laps] of index.byCar) {
    let last: LapDoc | null = null
    let best: number | null = null
    for (const l of laps) {
      const done = time(l.completedAt)
      if (done == null || done > t) continue
      if (!last || l.lap > last.lap) last = l
      const ms = l.lapTimeMs
      if (ms != null && ms > 0 && (best == null || ms < best)) best = ms
    }
    out.push({ car, last, crossedAt: last ? time(last.completedAt) : null, best, laps: last?.lap ?? 0 })
  }
  const byNumber = (a: CarAt, b: CarAt) => a.car.localeCompare(b.car, undefined, { numeric: true })
  out.sort((a, b) => {
    if (order === 'best') {
      if (a.best != null && b.best != null && a.best !== b.best) return a.best - b.best
      if ((a.best == null) !== (b.best == null)) return a.best == null ? 1 : -1
      if (a.laps !== b.laps) return b.laps - a.laps
      return byNumber(a, b)
    }
    if (a.laps !== b.laps) return b.laps - a.laps
    if (a.crossedAt != null && b.crossedAt != null && a.crossedAt !== b.crossedAt) return a.crossedAt - b.crossedAt
    return byNumber(a, b)
  })
  return out
}

/**
 * A lap cut at `at`: as stored if it had finished, the part run so far if it
 * was under way, and nothing if it had not started.
 *
 * A lap under way keeps only what was known while it was being driven — the
 * sectors already done — which is how the live laps collection shows the lap a
 * car is on.
 */
export function lapAt(lap: LapDoc, previous: LapDoc | undefined, at: number): LapDoc | null {
  const done = time(lap.completedAt)
  if (done != null && done <= at) return lap
  const start = time(lap.startedAt) ?? time(previous?.completedAt)
  if (start == null || start > at) return null
  const partial: LapDoc = { car: lap.car, lap: lap.lap }
  if (lap.startedAt) partial.startedAt = lap.startedAt
  if (lap.stintNumber != null) partial.stintNumber = lap.stintNumber
  if (lap.driver) partial.driver = lap.driver
  if (lap.class) partial.class = lap.class
  let through = start
  for (const n of [1, 2] as const) {
    const ms = lap[`s${n}Ms`]
    if (ms == null || ms <= 0 || through + ms > at) break
    through += ms
    partial[`s${n}Ms`] = ms
    if (lap[`s${n}Fcy`]) partial[`s${n}Fcy`] = true
    if (lap[`s${n}Sz`]) partial[`s${n}Sz`] = true
  }
  return partial
}

/** The session at `at`. */
export function replayFrame(index: ReplayIndex, at: Date, stamp: Stamp): ReplayFrame {
  const t = at.getTime()
  const src = index.source

  const laps: LapDoc[] = []
  const lapsByCar = new Map<string, LapDoc[]>()
  for (const [car, list] of index.byCar) {
    const cut: LapDoc[] = []
    for (let i = 0; i < list.length; i++) {
      const l = lapAt(list[i]!, list[i - 1], t)
      if (l) cut.push(l)
    }
    lapsByCar.set(car, cut)
    laps.push(...cut)
  }

  const pitStops = src.pitStops.filter((s) => (time(s.completedAt) ?? Infinity) <= t)
  const fcyPeriods = src.fcyPeriods
    .filter((p) => (time(p.startedAt) ?? Infinity) <= t)
    .map((p) => ((time(p.endedAt) ?? Infinity) > t ? { ...p, endedAt: null } : p))
  const said = (m: MessageDoc) => time(m.at) ?? time(m.receivedAt)
  const messages = src.messages
    .filter((m) => (said(m) ?? Infinity) <= t)
    .sort((a, b) => (time(b.receivedAt) ?? said(b) ?? 0) - (time(a.receivedAt) ?? said(a) ?? 0))
  const fuel = src.fuel.filter((f) => (time(f.at) ?? Infinity) <= t)

  return {
    at,
    session: sessionAt(src.session, at, fcyPeriods, stamp),
    standings: standingsAt(index, lapsByCar, pitStops, at, stamp),
    laps,
    messages,
    liveMessages: {
      messages: messages
        .filter((m) => t - (said(m) ?? 0) < replayBoardMs)
        .map((m, i) => ({
          id: `replay-${i}`,
          at: new Date(said(m) ?? t).toISOString(),
          message: m.message,
          ...(m.elapsedMs != null ? { elapsedMs: m.elapsedMs } : {}),
        })),
    },
    stints: [...lapsByCar].flatMap(([car, cut]) => {
      const stints = stintsFromLaps(cut, stamp)
      return stints.length > 0 ? [{ car, stints }] : []
    }),
    pitStops,
    fcyPeriods,
    fuel,
    carSummaries: [...lapsByCar].flatMap(([car, cut]) => {
      const s = carSummary(car, cut)
      return s ? [s] : []
    }),
  }
}

/**
 * The board at `at`: each car as it was at its latest crossing of the line.
 *
 * The position is worked out rather than read off the lap, because a lap's
 * position was the order at that car's own crossing, and two cars that crossed
 * a minute apart can both claim the same place.
 */
function standingsAt(
  index: ReplayIndex,
  lapsByCar: Map<string, LapDoc[]>,
  pitStops: PitStopDoc[],
  at: Date,
  stamp: Stamp,
): StandingsDoc {
  const t = at.getTime()
  const stopsUpTo = new Map<string, number>()
  for (const s of pitStops) stopsUpTo.set(s.car, (stopsUpTo.get(s.car) ?? 0) + 1)
  // In the pits: between a stop's pit-in and its pit-out. The stop is in the
  // source once it has finished, so this looks at all of them, not the cut.
  const inPits = new Set<string>()
  for (const s of index.source.pitStops) {
    const out = time(s.completedAt)
    if (out == null || out <= t) continue
    if (out - (s.stopLengthMs ?? 0) <= t) inPits.add(s.car)
  }

  const classCount = new Map<string, number>()
  const cars: CarStanding[] = ordered(index, at, index.order).map((c, i) => {
    const last = c.last
    const cut = lapsByCar.get(c.car) ?? []
    const now = cut[cut.length - 1]
    const under = now && !now.completedAt ? now : null
    const klass = last?.class ?? under?.class
    const standing: CarStanding = { number: c.car, position: i + 1 }
    const team = index.teams.get(c.car)
    if (team) standing.team = team
    const driver = under?.driver ?? last?.driver
    if (driver) standing.driver = driver
    if (klass) {
      const n = (classCount.get(klass) ?? 0) + 1
      classCount.set(klass, n)
      standing.class = klass
      standing.classPosition = n
    }
    standing.lap = c.laps
    standing.state = inPits.has(c.car) ? 'IN' : 'RUN'
    standing.lastLapMs = last?.lapTimeMs ?? null
    standing.bestLapMs = c.best
    standing.s1Ms = under?.s1Ms ?? null
    standing.s2Ms = under?.s2Ms ?? null
    standing.s3Ms = null
    standing.gapToLeaderMs = i === 0 ? null : (last?.gapToLeaderMs ?? null)
    if (i > 0 && last?.gapToLeaderLaps) standing.gapToLeaderLaps = last.gapToLeaderLaps
    standing.intervalAheadMs = i === 0 ? null : (last?.intervalAheadMs ?? null)
    if (i > 0 && last?.intervalAheadLaps) standing.intervalAheadLaps = last.intervalAheadLaps
    standing.stops = stopsUpTo.get(c.car) ?? 0
    return standing
  })
  return { updatedAt: stamp(at), cars }
}

/**
 * The session document at `at`: the clock counted to then, the track state
 * from whatever neutralisation was out, and the flag only once it had fallen.
 *
 * The clock is worked back from its final checkpoint, as a straight line from
 * the start. A red flag that stopped it is not in the data, so a replay across
 * one runs the clock straight through it.
 */
function sessionAt(
  session: StreamSessionDoc | null,
  at: Date,
  fcyPeriods: FcyPeriodDoc[],
  stamp: Stamp,
): StreamSessionDoc | null {
  if (!session) return null
  const t = at.getTime()
  const out: StreamSessionDoc = { ...session, lastSeenAt: stamp(at) }

  const finished = time(session.finishedAt)
  const over = finished != null && finished <= t
  if (!over) delete out.finishedAt
  if (session.clockStartAt && (time(session.clockStartAt) ?? 0) > t) delete out.clockStartAt

  const final = session.clock
  if (final?.at) {
    const finalAt = final.at.toDate().getTime()
    if (t < finalAt) {
      const zero = finalAt - final.elapsedMs
      const total = final.elapsedMs + final.remainingMs
      const elapsed = Math.max(0, t - zero)
      const clock: ClockCheckpointDoc = {
        at: stamp(at),
        elapsedMs: Math.min(elapsed, total),
        remainingMs: Math.max(0, total - elapsed),
        running: t >= zero && elapsed < total,
      }
      out.clock = clock
    }
  }

  if (!over) {
    const open = fcyPeriods.find((p) => !p.endedAt)
    out.trackState = open?.state ?? 'GREEN'
  }
  return out
}

/**
 * A car's stints from its laps, as the interpreter groups them: on the stint
 * number stamped on each lap, the last one open. See StintsFor in
 * timing/internal/store/stints.go, which this follows.
 */
export function stintsFromLaps(laps: readonly LapDoc[], stamp: Stamp): Stint[] {
  const out: Stint[] = []
  const byStint = new Map<number, Stint>()
  let previous = 0
  for (const l of [...laps].sort((a, b) => a.lap - b.lap)) {
    if (l.lap <= 0) continue
    // A lap with no number is the stint it followed, not the first one: a
    // stray lap after the flag carries none, and counted from zero it put
    // the race's last lap into its first stint.
    const number = l.stintNumber ?? previous
    previous = number
    let st = byStint.get(number)
    const start = l.startedAt ?? l.completedAt
    if (!st) {
      st = { number, firstLap: l.lap, lastLap: l.lap, laps: 0, totalMs: 0, greenLaps: 0, greenTotalMs: 0, driverLaps: {} }
      if (start) st.startedAt = stamp(start.toDate())
      byStint.set(number, st)
      out.push(st)
    }
    st.lastLap = l.lap
    if (!st.startedAt && start) st.startedAt = stamp(start.toDate())
    if (l.completedAt) st.endedAt = l.completedAt
    st.laps++
    if (l.driver) st.driverLaps![l.driver] = (st.driverLaps![l.driver] ?? 0) + 1
    const ms = l.lapTimeMs
    if (ms != null && ms > 0) {
      st.totalMs += ms
      const green = !l.inLap && !l.outLap && !l.s1Fcy && !l.s2Fcy && !l.s3Fcy && !l.s1Sz && !l.s2Sz && !l.s3Sz
      if (green) {
        st.greenLaps++
        st.greenTotalMs += ms
      }
      if (st.bestLapMs == null || ms < st.bestLapMs) st.bestLapMs = ms
    }
  }
  out.sort((a, b) => a.number - b.number)
  if (out.length > 0) out[out.length - 1]!.open = true
  return out
}

/** A car's session bests from its completed laps. */
export function carSummary(car: string, laps: readonly LapDoc[]): CarSummaryDoc | null {
  const done = laps.filter((l) => l.completedAt)
  if (done.length === 0) return null
  const out: CarSummaryDoc = { car, laps: done.length }
  const klass = done.find((l) => l.class)?.class
  if (klass) out.class = klass
  for (const l of done) {
    const ms = l.lapTimeMs
    if (ms != null && ms > 0 && (out.bestLapMs == null || ms < out.bestLapMs)) {
      out.bestLapMs = ms
      out.bestLapNum = l.lap
    }
    for (const n of [1, 2, 3] as const) {
      const s = l[`s${n}Ms`]
      const best = out[`bestS${n}Ms`]
      if (s != null && s > 0 && (best == null || s < best)) {
        out[`bestS${n}Ms`] = s
        out[`bestS${n}Lap`] = l.lap
      }
    }
  }
  const { bestS1Ms: a, bestS2Ms: b, bestS3Ms: c } = out
  out.idealLapMs = a != null && b != null && c != null ? a + b + c : null
  return out
}
